import React, { useMemo, useState } from "react";
import { List, Link, Button } from "@bookingcom/bui-react";
import { useI18n, t, Trans } from "@bookingcom/lingojs-react";
import { UICarrierBaggagePolicies } from "@flights/types";
import useModalDialog from "../../../hooks/useModalDialog";
import Frame from "../Frame";
import styles from "./SearchBaggagePolicies.module.css";
import useProfiler from "../../../hooks/useProfiler";
import useGetSearchResultsBound from "../../../hooks/useGetSearchResultsBound";
import { useSelector } from "react-redux";
import { getSearchResultsBaggagePolicies } from "../../../store/searchResults/selectors";
import { getSearchResultsBaggagePolicies as getOBSearchResultsBaggagePolicies } from "../../../store/searchResultsBounds/outboundSelectors";
import { getSearchResultsBaggagePolicies as getIBSearchResultsBaggagePolicies } from "../../../store/searchResultsBounds/inboundSelectors";

interface Props {
  text?: string;
  hideBaggagePolicies?: boolean;
}

const SearchBaggagePolicies: React.FunctionComponent<Props> = ({ hideBaggagePolicies }) => {
  const [active, setActive] = useState(false);
  const i18n = useI18n();
  const { isAmerican } = useProfiler();

  const Modal = useModalDialog();
  const bound = useGetSearchResultsBound();

  const bPolicies = useSelector(getSearchResultsBaggagePolicies);
  const bPoliciesOutbound = useSelector(getOBSearchResultsBaggagePolicies);
  const bPoliciesInbound = useSelector(getIBSearchResultsBaggagePolicies);
  const baggagePolicies = useMemo(() => {
    if (hideBaggagePolicies) {
      return undefined;
    }

    if (bound === "outbound") {
      return bPoliciesOutbound;
    }

    if (bound === "inbound") {
      return bPoliciesInbound;
    }

    return bPolicies;
  }, [bPolicies, bPoliciesInbound, bPoliciesOutbound, bound, hideBaggagePolicies]);

  if (!baggagePolicies && !isAmerican()) {
    return null;
  }

  const baggageList = baggagePolicies?.map((policy: UICarrierBaggagePolicies) => {
    return {
      content: (
        <Link
          href={policy.url}
          key={policy.code}
          text={policy.name}
          attributes={{ target: "_blank", "data-testid": "search_baggage_policies_modal_link" }}
        />
      )
    };
  });

  return (
    <>
      <Frame mb={4} className={styles.baggagePolicies} attributes={{ "data-testid": "search_baggage_policies" }}>
        {baggagePolicies && (
          <Trans
            tag="flights_sr_baggage_fee_discalimer"
            variables={{
              start_link: "<0>",
              end_link: "</0>"
            }}
            components={[
              <Button
                variant="tertiary"
                onClick={(e) => {
                  e.preventDefault();
                  setActive(true);
                }}
                className={styles.modalOpener}
                key={0}
              />
            ]}
          />
        )}
      </Frame>

      <Modal
        title={i18n.trans(t("flights_sr_baggage_modal_header"))}
        subtitle={i18n.trans(t("flights_sr_baggage_modal_text"))}
        active={active}
        onCloseTrigger={() => setActive(false)}
        position="center"
        showModalInMobile
        dataTestId="search_baggage_policies_modal"
      >
        <List divided={true} items={baggageList} />
      </Modal>
    </>
  );
};
export default SearchBaggagePolicies;
